import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Admin → Partner Shared Vacancies: review vacancies shared by partners, accept & forward to an
// Assistant Recruiter, or reject with a reason; follow accepted ones through to placement and commission.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAGE_SIZE = 10;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

const STATUS_STYLES = {
  Pending: 'bg-gray-100 text-gray-700 border-gray-200',
  Accepted: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  Rejected: 'bg-red-100 text-red-700 border-red-200'
};

const pageNumbers = (current, total) => {
  const pages = [];
  for (let n = 1; n <= total; n += 1) {
    if (n === 1 || n === total || Math.abs(n - current) <= 1) pages.push(n);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  return pages;
};

const ReviewModal = ({ vacancy, action, assistantRecruiters, onClose, onDone }) => {
  const [arId, setArId] = useState(assistantRecruiters.length === 1 ? assistantRecruiters[0].user_id : '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const accepting = action === 'accept';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/partner-vacancies/${vacancy.id}/${action}`,
        accepting ? { assistant_recruiter_user_id: arId, admin_note: note } : { admin_note: note },
        { headers: headers() }
      );
      onDone(res.data.vacancy);
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-white p-6 text-black shadow-xl">
        <h3 className="text-xl font-black">{accepting ? '✓ Accept & forward vacancy' : '✕ Reject vacancy'}</h3>
        <div className="mt-3 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm">
          <p className="font-black">{vacancy.company_name} · {vacancy.job_title} × {vacancy.total_vacancies}</p>
          <p className="text-gray-600">{vacancy.place}, {vacancy.district} · shared by {vacancy.partner_org_name}</p>
        </div>

        {accepting ? (
          <>
            <label className="mt-4 block text-sm font-bold">Forward to Assistant Recruiter
              <select value={arId} onChange={(e) => setArId(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2" required>
                <option value="">Choose Assistant Recruiter</option>
                {assistantRecruiters.map((s) => <option key={s.user_id} value={s.user_id}>{s.name} ({s.user_id})</option>)}
              </select>
            </label>
            {assistantRecruiters.length === 0 && <p className="mt-1 text-xs font-bold text-red-600">No Assistant Recruiter staff found. Create one in Staff first.</p>}
            <p className="mt-2 text-xs text-gray-500">The company will appear in their company call list tagged 🤝 Partner, and they get a notification.</p>
          </>
        ) : (
          <p className="mt-4 text-sm text-gray-600">The partner will see this reason on their dashboard.</p>
        )}

        <label className="mt-3 block text-sm font-bold">{accepting ? 'Note (optional)' : 'Reason'}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2" required={!accepting}
            placeholder={accepting ? 'Anything for the partner or recruiter…' : 'e.g. Company already in our list, number not reachable…'} />
        </label>

        {error && <p className="mt-2 text-sm font-bold text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl border px-4 py-2 font-bold">Cancel</button>
          <button type="submit" disabled={saving || (accepting && !arId)}
            className={`rounded-xl px-5 py-2 font-black text-white disabled:opacity-50 ${accepting ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}>
            {saving ? 'Saving…' : accepting ? 'Accept & Forward' : 'Reject'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Placements recorded without a vacancy can't be traced automatically; admin links them here
const LinkPlacementModal = ({ vacancy, onClose, onDone }) => {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/partner-vacancies/${vacancy.id}/placement-candidates`, { headers: headers() })
      .then((res) => setRows(res.data || []))
      .catch(() => setRows([]));
  }, [vacancy.id]);

  const link = async (row) => {
    if (!window.confirm(`Credit the placement of "${row.member_name}" to ${vacancy.partner_org_name}'s vacancy at ${vacancy.company_name}? The partner will get their commission for it.`)) return;
    setSavingId(row.workflow_id);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/admin/partner-vacancies/${vacancy.id}/link-placement`, { workflow_id: row.workflow_id }, { headers: headers() });
      onDone(res.data.vacancy);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to link placement.');
    } finally {
      setSavingId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const company = vacancy.company_name.toLowerCase();
  const shown = (rows || [])
    .filter((row) => !q || [row.member_name, row.job_company, row.interview_company, row.offered_job_title, row.job_title, row.contact_number]
      .some((v) => String(v || '').toLowerCase().includes(q)))
    // likely matches (same company name) first
    .sort((a, b) => Number([b.job_company, b.interview_company].some((v) => String(v || '').toLowerCase().includes(company)))
      - Number([a.job_company, a.interview_company].some((v) => String(v || '').toLowerCase().includes(company))));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white text-black shadow-xl">
        <div className="border-b p-5">
          <h3 className="text-xl font-black">🔗 Link a placed candidate</h3>
          <p className="mt-1 text-sm text-gray-600">
            Vacancy: <b>{vacancy.job_title}</b> at <b>{vacancy.company_name}</b>, shared by <b>{vacancy.partner_org_name}</b>.
            Use this when a candidate was placed there but the recruiter did not select this vacancy. The partner then gets their commission.
          </p>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search candidate, company, job…" className="mt-3 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
          {error && <p className="mt-2 text-sm font-bold text-red-600">{error}</p>}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {rows === null ? <p className="p-3 text-sm text-gray-500">Loading placed candidates…</p>
            : shown.length === 0 ? <p className="p-3 text-sm text-gray-500">No placed candidates waiting to be linked.</p>
            : shown.map((row) => {
              const likely = [row.job_company, row.interview_company].some((v) => String(v || '').toLowerCase().includes(company));
              return (
                <div key={row.workflow_id} className={`mb-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${likely ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200'}`}>
                  <div className="min-w-0">
                    <p className="font-black">{row.member_name} {likely && <span className="ml-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-black text-white">Likely match</span>}</p>
                    <p className="text-xs text-gray-600">
                      {[row.job_title || row.offered_job_title, row.job_company || row.interview_company || 'no company recorded', row.work_location].filter(Boolean).join(' · ')}
                    </p>
                    <p className="text-xs text-gray-400">
                      Placed {String(row.placed_at || row.updated_at || '').slice(0, 10)} · Service charge {Number(row.service_charge_amount) > 0 ? `₹${row.service_charge_amount}` : 'not recorded'}{row.recruiter_name ? ` · by ${row.recruiter_name}` : ''}
                    </p>
                  </div>
                  <button type="button" onClick={() => link(row)} disabled={savingId !== null}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-black text-white hover:bg-emerald-700 disabled:opacity-50">
                    {savingId === row.workflow_id ? 'Linking…' : 'Link & credit'}
                  </button>
                </div>
              );
            })}
        </div>
        <div className="border-t p-4 text-right">
          <button type="button" onClick={onClose} className="rounded-xl border px-4 py-2 font-bold">Close</button>
        </div>
      </div>
    </div>
  );
};

const AdminPartnerVacancies = ({ onPendingCount }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assistantRecruiters, setAssistantRecruiters] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '', partner: '' });
  const [page, setPage] = useState(1);
  const [review, setReview] = useState(null); // { vacancy, action }
  const [linking, setLinking] = useState(null); // vacancy whose placement is being linked

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/partner-vacancies`, { headers: headers() })
      .then((res) => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/admin/staff`, { headers: headers() })
      .then((res) => setAssistantRecruiters((res.data || []).filter((s) => s.role === 'Assistant Recruiter')))
      .catch(() => {});
  }, []);

  useEffect(() => { onPendingCount?.(rows.filter((r) => r.status === 'Pending').length); }, [rows]); // eslint-disable-line

  const partnerOptions = [...new Set(rows.map((r) => r.partner_org_name).filter(Boolean))].sort();
  const q = filters.search.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (filters.status && r.status !== filters.status) return false;
    if (filters.partner && r.partner_org_name !== filters.partner) return false;
    if (q && ![r.company_name, r.job_title, r.place, r.district, r.contact_person, r.company_type, ...(r.contact_numbers || [])]
      .some((v) => String(v || '').toLowerCase().includes(q))) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const summary = {
    total: filtered.length,
    pending: filtered.filter((r) => r.status === 'Pending').length,
    accepted: filtered.filter((r) => r.status === 'Accepted').length,
    rejected: filtered.filter((r) => r.status === 'Rejected').length,
    placements: filtered.reduce((sum, r) => sum + Number(r.placements || 0), 0),
    commission: filtered.reduce((sum, r) => sum + Number(r.commission_total || 0), 0)
  };
  const setFilter = (e) => { setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value })); setPage(1); };

  return (
    <div className="space-y-6">
      <div className="rounded-[24px] border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-xl font-black text-black">Partner Shared Vacancies</h3>
            <p className="text-sm text-gray-500">Vacancies reported by partners. Accept to send them to an Assistant Recruiter; partners earn their commission on placements.</p>
          </div>
          <button type="button" onClick={load} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-black hover:border-yellow-400">↺ Refresh</button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[
            { label: 'Total Shared', value: summary.total, icon: '🏢', cls: 'from-slate-800 to-black text-white', sub: 'text-yellow-300' },
            { label: 'Pending Review', value: summary.pending, icon: '⏳', cls: 'from-orange-400 to-rose-500 text-white', sub: 'text-orange-50' },
            { label: 'Accepted', value: summary.accepted, icon: '✅', cls: 'from-emerald-400 to-emerald-600 text-white', sub: 'text-emerald-50' },
            { label: 'Rejected', value: summary.rejected, icon: '✕', cls: 'from-gray-300 to-gray-500 text-white', sub: 'text-gray-50' },
            { label: 'Placements', value: summary.placements, icon: '🏆', cls: 'from-yellow-300 to-amber-500 text-black', sub: 'text-black/70' },
            { label: 'Commission', value: `₹${summary.commission.toFixed(0)}`, icon: '💰', cls: 'from-violet-400 to-purple-600 text-white', sub: 'text-violet-50' }
          ].map((c) => (
            <div key={c.label} className={`relative overflow-hidden rounded-2xl bg-gradient-to-br p-4 shadow-md ${c.cls}`}>
              <span className="absolute -right-2 -top-2 text-5xl opacity-20" aria-hidden="true">{c.icon}</span>
              <div className={`text-xs font-extrabold uppercase tracking-[0.14em] ${c.sub}`}>{c.label}</div>
              <div className="mt-2 text-3xl font-black">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 rounded-2xl border border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50 p-3 sm:grid-cols-3">
          <input name="search" value={filters.search} onChange={setFilter} placeholder="🔍 Company, job, place, contact" className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm" />
          <select name="partner" value={filters.partner} onChange={setFilter} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm">
            <option value="">All partners</option>
            {partnerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select name="status" value={filters.status} onChange={setFilter} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm">
            <option value="">All statuses</option>
            <option value="Pending">Pending review</option>
            <option value="Accepted">Accepted</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {loading ? (
          <p className="mt-4 text-gray-600">Loading shared vacancies…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-600">
            {rows.length === 0 ? 'No partner has shared a vacancy yet.' : 'No vacancies match these filters.'}
          </div>
        ) : (
          <>
            <div className="mt-4 overflow-x-auto rounded-[20px] border border-gray-200">
              <table className="w-full min-w-[1300px] text-left text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Shared', 'Partner', 'Company', 'Vacancy', 'Location', 'Contact', 'Dates', 'Status', 'Progress', 'Actions'].map((h) => (
                      <th key={h} className="border-b border-gray-200 px-3 py-2.5 font-extrabold text-gray-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((v) => (
                    <tr key={v.id} className="align-top odd:bg-white even:bg-gray-50/60">
                      <td className="border-b border-gray-200 px-3 py-3 text-xs text-gray-600 whitespace-nowrap">{String(v.created_at || '').slice(0, 16)}</td>
                      <td className="border-b border-gray-200 px-3 py-3">
                        <p className="font-bold text-black">{v.partner_org_name}</p>
                        <p className="text-xs text-gray-500">Login: {v.partner_user_id || '-'} · {v.commission_percentage ? `${v.commission_percentage}%` : 'no %'}</p>
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3">
                        <p className="font-bold text-black">{v.company_name}</p>
                        <p className="text-xs text-gray-500">{v.company_type}</p>
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3">
                        <p className="font-bold text-black">{v.job_title}</p>
                        <p className="text-xs text-gray-500">{v.total_vacancies} opening{Number(v.total_vacancies) === 1 ? '' : 's'}</p>
                        {v.remarks && <p className="mt-1 max-w-[200px] text-xs text-gray-600">💬 {v.remarks}</p>}
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3 text-gray-700">{v.place}, {v.district}<p className="text-xs text-gray-400">{v.state}</p></td>
                      <td className="border-b border-gray-200 px-3 py-3 text-gray-700">
                        <p className="font-semibold">{v.contact_person}</p>
                        {(v.contact_numbers || []).map((n) => <a key={n} href={`tel:${n}`} className="block text-xs font-bold text-blue-600 underline">{n}</a>)}
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                        <p>Reported: {v.reported_date}</p>
                        <p className={v.last_joining_date ? 'font-bold text-orange-700' : ''}>{v.last_joining_date ? `Last date: ${v.last_joining_date}` : 'No last date'}</p>
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3">
                        <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-black ${STATUS_STYLES[v.status] || STATUS_STYLES.Pending}`}>{v.status}</span>
                        {v.forwarded_to_name && <p className="mt-1 text-xs text-gray-500">→ {v.forwarded_to_name}</p>}
                        {v.admin_note && <p className="mt-1 max-w-[180px] text-xs text-gray-500">Note: {v.admin_note}</p>}
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3 text-xs text-gray-600">
                        {v.status === 'Accepted' ? (
                          <>
                            <p>AR status: <b className="text-black">{v.ar_status || 'Pending'}</b></p>
                            <p>Vacancies recorded: <b className="text-black">{v.recorded_vacancies || 0}</b></p>
                            <p>Placed: <b className="text-black">{v.placements || 0}</b></p>
                            {Number(v.commission_total) > 0 && <p className="font-black text-purple-700">Commission ₹{Number(v.commission_total).toFixed(2)}</p>}
                          </>
                        ) : '—'}
                      </td>
                      <td className="border-b border-gray-200 px-3 py-3">
                        {v.status === 'Accepted' ? (
                          <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-bold text-gray-400">Forwarded ✓</span>
                            <button type="button" onClick={() => setLinking(v)} className="whitespace-nowrap rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-extrabold text-emerald-700 hover:bg-emerald-100">🔗 Link placed candidate</button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            <button type="button" onClick={() => setReview({ vacancy: v, action: 'accept' })} className="rounded-xl bg-emerald-500 px-3 py-1.5 text-xs font-extrabold text-white hover:bg-emerald-600">✓ Accept & Forward</button>
                            {v.status === 'Pending' && (
                              <button type="button" onClick={() => setReview({ vacancy: v, action: 'reject' })} className="rounded-xl bg-red-100 px-3 py-1.5 text-xs font-extrabold text-red-700 hover:bg-red-200">✕ Reject</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-bold disabled:opacity-40">« First</button>
                <button type="button" onClick={() => setPage(safePage - 1)} disabled={safePage === 1} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-bold disabled:opacity-40">‹ Prev</button>
                {pageNumbers(safePage, totalPages).map((n, i) => (n === '…'
                  ? <span key={`gap-${i}`} className="px-1 text-gray-400">…</span>
                  : (
                    <button key={n} type="button" onClick={() => setPage(n)}
                      className={`min-w-[36px] rounded-lg px-3 py-1.5 text-sm font-extrabold ${n === safePage ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black shadow-md' : 'border border-gray-300 bg-white'}`}>
                      {n}
                    </button>
                  )))}
                <button type="button" onClick={() => setPage(safePage + 1)} disabled={safePage === totalPages} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-bold disabled:opacity-40">Next ›</button>
                <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-bold disabled:opacity-40">Last »</button>
              </div>
            </div>
          </>
        )}
      </div>

      {linking && (
        <LinkPlacementModal
          vacancy={linking}
          onClose={() => setLinking(null)}
          onDone={(updated) => {
            setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
            setLinking(null);
          }}
        />
      )}

      {review && (
        <ReviewModal
          vacancy={review.vacancy}
          action={review.action}
          assistantRecruiters={assistantRecruiters}
          onClose={() => setReview(null)}
          onDone={(updated) => {
            setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            setReview(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminPartnerVacancies;
